import React from 'react';

import DataGrid from '@gogame/data-grid';
import { useChildComponent } from '@gogame/hooks-ts';
import {
  useScreenDispatch,
  useScreenGo,
  useScreenState,
} from '@gogame/screens';

import { ParentCampaignActions } from 'scenes/Campaigns/ParentCampaigns/components/ParentCampaignActions';
import {
  ParentCampaignFormEdit,
  ParentCampaignFormDuplicateWithChaining,
} from 'scenes/Campaigns/ParentCampaigns/components/ParentCampaignForm';
import {
  columnIDProps,
  columnNameProps,
  columnCampaignsProps,
  columnCountryProps,
  columnAffiliateProps,
  columnNetworkProps,
  columnPlatformProps,
  columnApplicationProps,
  columnActivityTypeProps,
  columnFraudProtectionProps,
  columnExpirationDateProps,
  columnPromoCodeProps,
  columnLastUpdatedProps,
  columnCreatedByProps,
  columnCreatedProps,
  columnActionsProps,
  columnHasCostModelsProps,
  columnHasRewardsProps,
} from 'scenes/Campaigns/components/Columns';
import { useLoadParentCampaigns } from 'scenes/Campaigns/ParentCampaigns/data';
import {
  SET_TABLE_SETTINGS,
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
} from 'scenes/Campaigns/ParentCampaigns/screenReducer';
import AffiliateDetails from 'scenes/Affiliates/components/AffiliateDetails';
import { parentCampaignDetailsLink } from 'scenes/Campaigns/ParentCampaigns/navigation';
import CampaignDetailsLink from 'scenes/Campaigns/components/CampaignDetailsLink';
import { campaignDetailsLink } from 'scenes/Campaigns/GGCampaigns/navigation';

import {
  COLUMN_CREATED_DATE,
  COLUMN_UPDATED_DATE,
} from 'scenes/Campaigns/constants';
import Toolbar from './Toolbar';

const ParentCampaignsDataTable = () => {
  const [childComponent, openChildComponent] = useChildComponent();

  const go = useScreenGo();

  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const { filters, tableSettings, searchQuery, ready } = screenState;
  const { data, isLoading, isRefetching } = useLoadParentCampaigns(
    {
      filters,
      searchQuery,
      tableSettings,
    },
    { enabled: ready }
  );

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleEdit = ({ id, name }) => {
    openChildComponent(ParentCampaignFormEdit, {
      name: name,
      id: id,
    });
  };

  const handleDuplicate = ({ id }) => {
    openChildComponent(ParentCampaignFormDuplicateWithChaining, {
      id: id,
      onSubmit: ([campaign]) => {
        if (campaign?.parentCampaign?.id) {
          go(`${campaign.parentCampaign.id}`);
          return;
        }

        go(
          `../${campaignDetailsLink.absolutePath.replace(':id', campaign.id)}`
        );
      },
    });
  };

  const handleOpenAffiliate = (_, { id }) => {
    openChildComponent(AffiliateDetails, {
      id,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  return (
    <React.Fragment>
      {childComponent}
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        loading={isLoading}
        fetching={isRefetching}
        ready={ready}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={<Toolbar />}
        storageKey={'parent-campaigns'}
        defaultColumnWidth={120}
        filtered={!!screenState.searchQuery}
      >
        <DataGrid.Column {...columnIDProps} />
        <DataGrid.Column
          {...columnNameProps}
          linkComponent={({ children, row }) => (
            <CampaignDetailsLink
              row={row}
              detailsLink={parentCampaignDetailsLink}
            >
              {children}
            </CampaignDetailsLink>
          )}
        />
        <DataGrid.Column {...columnCampaignsProps} />
        <DataGrid.Column
          {...columnAffiliateProps}
          onLinkClick={handleOpenAffiliate}
        />
        <DataGrid.Column {...columnNetworkProps} sortable={false} />
        <DataGrid.Column {...columnPlatformProps} sortable={false} />
        <DataGrid.Column {...columnCountryProps} sortable={false} />
        <DataGrid.Column {...columnApplicationProps} />
        <DataGrid.Column {...columnActivityTypeProps} sortable={false} />
        <DataGrid.Column {...columnHasRewardsProps} sortable={false} />
        <DataGrid.Column {...columnHasCostModelsProps} sortable={false} />
        <DataGrid.Column {...columnFraudProtectionProps} sortable={false} />
        <DataGrid.Column {...columnExpirationDateProps} sortable={false} />
        <DataGrid.Column
          {...columnPromoCodeProps}
          type={'bool'}
          align={'center'}
          getValue={(v) => Boolean(v)}
          sortable={false}
        />
        <DataGrid.Column
          {...columnLastUpdatedProps}
          field={COLUMN_UPDATED_DATE}
        />
        <DataGrid.Column {...columnCreatedByProps} />
        <DataGrid.Column {...columnCreatedProps} field={COLUMN_CREATED_DATE} />
        <DataGrid.Column
          {...columnActionsProps}
          actionsComponent={ParentCampaignActions}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          limit={2}
        />
      </DataGrid>
    </React.Fragment>
  );
};

export default ParentCampaignsDataTable;
